import type { MeasurementFreshness } from '../streamRecovery/contracts';
import { FRAME_EVIDENCE_STALE_MS } from '../streamRecovery/policy';

/** Admits progressing observations without comparing clocks on different machines. */
export class ReportFreshnessGate {
  private epoch = -1;
  private sequence = -1;
  private observation = -1;
  private receivedAt?: number;
  private producerAt?: number;
  private observationAt?: number;

  public accept(input: {
    epoch: number; sequence: number; observationSequence?: number; observationAgeMs?: number;
    receivedAtMs: number; producerTimestampMs?: number; trustedTransitAgeMs?: number;
  }): MeasurementFreshness {
    if (input.epoch < this.epoch || (input.epoch === this.epoch && input.sequence <= this.sequence)) return { state: 'stale' };
    if (input.epoch > this.epoch) this.reset(input.epoch);
    const receiptDelta = this.receivedAt === undefined ? undefined : input.receivedAtMs - this.receivedAt;
    const sourceDelta = this.producerAt === undefined || input.producerTimestampMs === undefined ? undefined : input.producerTimestampMs - this.producerAt;
    this.sequence = input.sequence;
    this.receivedAt = input.receivedAtMs;
    this.producerAt = input.producerTimestampMs;
    const progressed = input.observationSequence !== undefined && input.observationSequence > this.observation;
    if (progressed) { this.observation = input.observationSequence!; this.observationAt = input.receivedAtMs; }
    const age = input.observationAgeMs;
    if (age === undefined || !Number.isFinite(age) || age < 0) return { state: 'unavailable' };
    const heldAge = !progressed && this.observationAt !== undefined ? input.receivedAtMs - this.observationAt : 0;
    const totalAge = Math.max(age, heldAge) + (input.trustedTransitAgeMs ?? 0);
    if (!progressed || totalAge > FRAME_EVIDENCE_STALE_MS) return { state: 'stale', ageMs: totalAge, sequence: this.observation };
    const trusted = input.trustedTransitAgeMs !== undefined && input.trustedTransitAgeMs >= 0;
    // A thawed queue advances source time rapidly with nearly no receipt time. Do not let
    // those new sequence numbers reset either frame freshness or a healthy dwell.
    const paced = receiptDelta !== undefined && sourceDelta !== undefined && receiptDelta >= 250 && receiptDelta <= FRAME_EVIDENCE_STALE_MS && sourceDelta > 0 && sourceDelta <= receiptDelta + 500;
    return { state: trusted || paced ? 'fresh' : 'unavailable', ageMs: totalAge, sequence: this.observation };
  }

  public reset(epoch: number): void {
    this.epoch = epoch;
    this.sequence = -1;
    this.observation = -1;
    this.receivedAt = undefined;
    this.producerAt = undefined;
    this.observationAt = undefined;
  }
}
